import { useState, useEffect, useRef } from "react";
import { api } from "../../api";
import type {
  AssetFullDetail,
  ClientRow,
  FieldDef,
  Identity,
  MediaRow,
  ChangeRequestFromMe,
} from "../../types";
import Icon from "../../components/Icon";
import Badge from "../../components/Badge";

type ClientAssetViewProps = {
  clientId: string;
  clientRow: ClientRow | null;
  identity: Identity;
  assetId: string;
  onBack: () => void;
  onShowToast: (msg: string) => void;
};

type FieldOptions = { value: string; label: string }[];

function getFieldOptions(fd: FieldDef): FieldOptions {
  if (!fd.options) return [];
  try {
    const arr = JSON.parse(fd.options) as string[];
    return arr.map((o) => ({ value: o, label: o }));
  } catch {
    return [];
  }
}

function pubStateToBadge(s: string | null): "published" | "draft" | "archived" {
  if (s === "published") return "published";
  if (s === "archived") return "archived";
  return "draft";
}

function relTime(iso: string | null | undefined): string {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.max(0, Math.round((now - then) / 1000));
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? "" : "s"} ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? "" : "s"} ago`;
  const d = Math.round(hr / 24);
  return `${d} day${d === 1 ? "" : "s"} ago`;
}

function slotForMedia(m: MediaRow): string {
  if (m.role === "hero" || m.is_primary === 1) return "hero";
  if (m.role) return `${m.role}_${m.sort_order}`;
  return `gallery_${m.sort_order}`;
}

const FIELD_LABEL: React.CSSProperties = {
  display: "block",
  fontSize: 11,
  fontFamily: "var(--font-mono)",
  textTransform: "uppercase",
  letterSpacing: "0.1em",
  color: "var(--fg-muted)",
  marginBottom: 6,
  fontWeight: 500,
};

const PENDING_NOTE: React.CSSProperties = {
  marginTop: 6,
  padding: "6px 10px",
  background: "rgba(244,196,90,0.08)",
  borderLeft: "2px solid var(--warm-bright)",
  fontSize: 12,
  color: "var(--fg-muted)",
  fontFamily: "var(--font-mono)",
  borderRadius: 2,
  display: "flex",
  alignItems: "center",
  gap: 6,
};

const VALUE_DISPLAY: React.CSSProperties = {
  padding: "10px 12px",
  border: "1px solid var(--rule-faint)",
  borderRadius: "var(--r-sm)",
  background: "var(--surface)",
  fontSize: 14,
  color: "var(--fg)",
  minHeight: 38,
  display: "flex",
  alignItems: "center",
};

export default function ClientAssetView({
  clientId: _clientId,
  clientRow,
  identity,
  assetId,
  onBack,
  onShowToast,
}: ClientAssetViewProps) {
  const canEdit =
    identity.role === "client_editor" || identity.role === "client_owner";

  const [detail, setDetail] = useState<AssetFullDetail | null>(null);
  const [fieldDefs, setFieldDefs] = useState<FieldDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [myCrs, setMyCrs] = useState<ChangeRequestFromMe[]>([]);

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([
      api.getFieldDefs(_clientId),
      api.getAsset(assetId),
      api
        .myChangeRequests({ asset_id: assetId, status: "pending" })
        .catch(() => ({ change_requests: [] as ChangeRequestFromMe[] })),
    ])
      .then(([fd, det, crs]) => {
        setFieldDefs(fd.field_defs);
        setDetail(det);
        setMyCrs(crs.change_requests);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, [_clientId, assetId]);

  function refreshCrs() {
    api
      .myChangeRequests({ asset_id: assetId, status: "pending" })
      .then((res) => setMyCrs(res.change_requests))
      .catch(() => {
        // non-fatal — the pending indicator just won't show until next mount
      });
  }

  const fieldsByName: Record<string, string> = detail?.fields ?? {};
  const pendingFieldCrs: Record<string, ChangeRequestFromMe[]> = {};
  const pendingImageCrs: Record<string, ChangeRequestFromMe[]> = {};
  for (const cr of myCrs) {
    if (cr.status !== "pending") continue;
    if (cr.op_type === "field_update" && cr.field_name) {
      (pendingFieldCrs[cr.field_name] ??= []).push(cr);
    } else if (cr.op_type === "image_update" && cr.image_slot) {
      (pendingImageCrs[cr.image_slot] ??= []).push(cr);
    }
  }

  const title = detail?.asset.title ?? "…";

  return (
    <div className="page page-enter" style={{ padding: "24px 32px 64px" }}>
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <button
          onClick={onBack}
          className="btn btn-quiet btn-sm"
          style={{ marginLeft: -6, marginBottom: 20 }}
        >
          <Icon name="arrow_left" size={14} /> Back to catalog
        </button>

        {loading ? (
          <ClientSkeleton />
        ) : error ? (
          <ErrorBanner message={error} />
        ) : detail ? (
          <>
            <div
              style={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                gap: 24,
                marginBottom: 28,
              }}
            >
              <div>
                <span className="eyebrow">Asset</span>
                <h1 className="t-h1" style={{ fontSize: 32, marginTop: 8 }}>
                  {title}
                </h1>
                <div style={{ marginTop: 10 }}>
                  <Badge kind={pubStateToBadge(detail.asset.publication_state)}>
                    {detail.asset.publication_state ?? "draft"}
                  </Badge>
                  {clientRow && (
                    <span
                      style={{
                        marginLeft: 10,
                        fontSize: 12,
                        color: "var(--fg-subtle)",
                        fontFamily: "var(--font-mono)",
                      }}
                    >
                      {clientRow.name}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Fields */}
            {fieldDefs.length > 0 && (
              <section className="field-group" style={{ marginBottom: 20 }}>
                <div className="field-group-head">
                  <span className="eyebrow">Fields</span>
                </div>
                {fieldDefs.map((fd) => (
                  <ClientFieldRow
                    key={fd.field_name}
                    fd={fd}
                    value={fieldsByName[fd.field_name] ?? ""}
                    canEdit={canEdit}
                    pendingCrs={pendingFieldCrs[fd.field_name] ?? []}
                    assetId={assetId}
                    onSubmitted={() => {
                      onShowToast(
                        "Request submitted. Mark will apply it and let you know."
                      );
                      refreshCrs();
                    }}
                    onError={(msg) => onShowToast(msg)}
                  />
                ))}
              </section>
            )}

            {/* Media slots */}
            {detail.media.length > 0 && (
              <section className="field-group">
                <div className="field-group-head">
                  <span className="eyebrow">Photos</span>
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns:
                      "repeat(auto-fill, minmax(220px, 1fr))",
                    gap: 16,
                  }}
                >
                  {detail.media.map((m) => {
                    const slot = slotForMedia(m);
                    return (
                      <ClientImageSlot
                        key={m.id}
                        media={m}
                        slot={slot}
                        canEdit={canEdit}
                        pendingCrs={pendingImageCrs[slot] ?? []}
                        assetId={assetId}
                        onSubmitted={() => {
                          onShowToast(
                            "Image request submitted. Mark will update your catalog when he applies it."
                          );
                          refreshCrs();
                        }}
                        onError={(msg) => onShowToast(msg)}
                      />
                    );
                  })}
                </div>
              </section>
            )}

            {detail.media.length === 0 && fieldDefs.length === 0 && (
              <div className="empty card" style={{ padding: "60px 24px" }}>
                <h4 className="t-h4">No editable content on this asset yet.</h4>
                <p style={{ marginTop: 8 }}>
                  Reach out to Mark and he&apos;ll get this set up.
                </p>
              </div>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}

// --- Field row -----------------------------------------------------------

function ClientFieldRow({
  fd,
  value,
  canEdit,
  pendingCrs,
  assetId,
  onSubmitted,
  onError,
}: {
  fd: FieldDef;
  value: string;
  canEdit: boolean;
  pendingCrs: ChangeRequestFromMe[];
  assetId: string;
  onSubmitted: () => void;
  onError: (msg: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  function open() {
    setDraft(value);
    setNote("");
    setEditing(true);
  }

  function close() {
    setEditing(false);
    setDraft(value);
    setNote("");
  }

  async function submit() {
    const trimmed = draft.trim();
    if (!trimmed) {
      onError("Value can't be empty.");
      return;
    }
    setSubmitting(true);
    try {
      await api.submitFieldChangeRequest(assetId, {
        field_name: fd.field_name,
        new_value: trimmed,
        note: note.trim() || undefined,
      });
      setEditing(false);
      onSubmitted();
    } catch (e: unknown) {
      onError(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  const displayValue = value || "—";
  const options = fd.field_type === "select" ? getFieldOptions(fd) : null;
  const isBool = fd.field_type === "boolean";

  return (
    <div style={{ marginBottom: 16 }}>
      <label style={FIELD_LABEL}>
        {fd.display_name}
        {fd.required === 1 && (
          <span style={{ color: "var(--barn)" }}> *</span>
        )}
      </label>

      {!editing ? (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ ...VALUE_DISPLAY, flex: 1 }}>{displayValue}</div>
          {canEdit && (
            <button
              className="btn btn-quiet btn-sm"
              onClick={open}
              title="Submit a change for this field"
              style={{ flexShrink: 0 }}
            >
              <Icon name="pencil" size={13} /> Submit change
            </button>
          )}
        </div>
      ) : (
        <div
          style={{
            background: "var(--surface-raised)",
            border: "1px solid var(--rule)",
            borderRadius: "var(--r-sm)",
            padding: 14,
          }}
        >
          {options ? (
            <select
              className="input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              autoFocus
            >
              <option value="">—</option>
              {options.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          ) : isBool ? (
            <select
              className="input"
              value={draft === "true" || draft === "1" ? "true" : "false"}
              onChange={(e) => setDraft(e.target.value)}
              autoFocus
            >
              <option value="true">true</option>
              <option value="false">false</option>
            </select>
          ) : fd.field_type === "textarea" ? (
            <textarea
              className="input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              rows={4}
              style={{ resize: "vertical" }}
              autoFocus
            />
          ) : (
            <input
              className="input"
              type={fd.field_type === "number" ? "number" : "text"}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              autoFocus
            />
          )}

          <div style={{ marginTop: 10 }}>
            <label
              style={{ ...FIELD_LABEL, marginBottom: 4 }}
              htmlFor={`note-${fd.field_name}`}
            >
              Note (optional)
            </label>
            <textarea
              id={`note-${fd.field_name}`}
              className="input"
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 500))}
              rows={2}
              placeholder="Add context for Mark, or leave blank."
              style={{ resize: "vertical" }}
            />
            {note.length > 0 && (
              <div
                style={{
                  textAlign: "right",
                  marginTop: 4,
                  fontSize: 11,
                  fontFamily: "var(--font-mono)",
                  color:
                    note.length >= 450
                      ? "var(--warm-bright)"
                      : "var(--fg-subtle)",
                }}
              >
                {500 - note.length} left
              </div>
            )}
          </div>

          <div style={{ display: "flex", gap: 6, marginTop: 12 }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={() => void submit()}
              disabled={submitting}
            >
              {submitting ? "Submitting…" : "Submit request"}
            </button>
            <button
              className="btn btn-quiet btn-sm"
              onClick={close}
              disabled={submitting}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {pendingCrs.map((cr) => (
        <div key={cr.id} style={PENDING_NOTE}>
          <Icon name="inbox" size={12} />
          <span>
            Pending change submitted —{" "}
            <strong style={{ color: "var(--fg)" }}>
              {cr.new_value ?? "(no value)"}
            </strong>{" "}
            · {relTime(cr.submitted_at)}
          </span>
        </div>
      ))}
    </div>
  );
}

// --- Image slot ----------------------------------------------------------

function ClientImageSlot({
  media,
  slot,
  canEdit,
  pendingCrs,
  assetId,
  onSubmitted,
  onError,
}: {
  media: MediaRow;
  slot: string;
  canEdit: boolean;
  pendingCrs: ChangeRequestFromMe[];
  assetId: string;
  onSubmitted: () => void;
  onError: (msg: string) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const [note, setNote] = useState("");

  function pick() {
    fileInputRef.current?.click();
  }

  async function onFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      onError("Image must be 10 MB or smaller.");
      return;
    }
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      onError("Image must be JPEG, PNG, or WebP.");
      return;
    }

    setUploading(true);
    try {
      await api.submitImageChangeRequest(assetId, file, {
        image_slot: slot,
        note: note.trim() || undefined,
      });
      setNote("");
      setNoteOpen(false);
      onSubmitted();
    } catch (err: unknown) {
      onError(err instanceof Error ? err.message : String(err));
    } finally {
      setUploading(false);
    }
  }

  return (
    <div
      style={{
        border: "1px solid var(--rule-faint)",
        borderRadius: "var(--r-sm)",
        padding: 12,
        background: "var(--surface)",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      <div
        style={{
          aspectRatio: "1 / 1",
          background: "var(--surface-sunk)",
          borderRadius: "var(--r-sm)",
          overflow: "hidden",
          position: "relative",
        }}
      >
        <img
          src={media.image_url}
          alt={media.alt_text ?? ""}
          loading="lazy"
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      </div>
      <div
        style={{
          fontSize: 11,
          fontFamily: "var(--font-mono)",
          color: "var(--fg-muted)",
          textTransform: "uppercase",
          letterSpacing: "0.08em",
        }}
      >
        Slot: {slot}
      </div>

      {canEdit && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => void onFile(e)}
            style={{ display: "none" }}
          />
          {noteOpen && (
            <textarea
              className="input"
              value={note}
              onChange={(e) => setNote(e.target.value.slice(0, 500))}
              rows={2}
              placeholder="Note for Mark (optional)"
              style={{ fontSize: 13, resize: "vertical" }}
            />
          )}
          <div style={{ display: "flex", gap: 6 }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={pick}
              disabled={uploading}
              style={{ flex: 1, justifyContent: "center" }}
            >
              {uploading ? (
                "Uploading…"
              ) : (
                <>
                  <Icon name="upload" size={12} /> Replace
                </>
              )}
            </button>
            <button
              className="btn btn-quiet btn-sm"
              onClick={() => setNoteOpen((v) => !v)}
              disabled={uploading}
              title="Add a note"
            >
              {noteOpen ? "Hide note" : "Add note"}
            </button>
          </div>
        </>
      )}

      {pendingCrs.map((cr) => (
        <div key={cr.id} style={PENDING_NOTE}>
          <Icon name="inbox" size={12} />
          <span>Pending image change · {relTime(cr.submitted_at)}</span>
        </div>
      ))}
    </div>
  );
}

// --- Helpers -------------------------------------------------------------

function ClientSkeleton() {
  return (
    <div>
      <div
        className="skeleton"
        style={{ height: 12, width: 60, marginBottom: 12, borderRadius: 3 }}
      />
      <div
        className="skeleton"
        style={{ height: 34, width: "45%", marginBottom: 24, borderRadius: 4 }}
      />
      <div className="field-group">
        <div className="field-group-head">
          <div
            className="skeleton"
            style={{ height: 10, width: 50, borderRadius: 3 }}
          />
        </div>
        {[1, 2, 3].map((i) => (
          <div key={i} style={{ marginBottom: 16 }}>
            <div
              className="skeleton"
              style={{ height: 10, width: 80, marginBottom: 8, borderRadius: 3 }}
            />
            <div
              className="skeleton"
              style={{ height: 38, borderRadius: "var(--r-sm)" }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function ErrorBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      style={{
        padding: "16px 20px",
        background: "rgba(229,138,109,0.10)",
        border: "1px solid rgba(229,138,109,0.35)",
        borderRadius: "var(--r-md)",
        color: "#e58a6d",
        fontFamily: "var(--font-mono)",
        fontSize: 13,
        display: "flex",
        alignItems: "center",
        gap: 10,
      }}
    >
      <Icon name="x" size={14} />
      {message}
    </div>
  );
}
